/* ==========================================================================
   TUCANO · Search modal
   - Open: any [data-tcn-search-trigger] click
   - Close: ESC, close button, backdrop click
   - Predictive: /search/suggest.json (debounced)
   - Recent: localStorage (max 5)
   ========================================================================== */

(() => {
  'use strict';

  const RECENT_KEY = 'tcn-recent-searches';
  const RECENT_MAX = 5;
  const DEBOUNCE_MS = 220;

  function loadRecent() {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  }
  function saveRecent(arr) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, RECENT_MAX))); } catch (_) {}
  }
  function addRecent(q) {
    const term = (q || '').trim();
    if (!term) return;
    const list = loadRecent().filter((r) => r.toLowerCase() !== term.toLowerCase());
    list.unshift(term);
    saveRecent(list);
  }
  function removeRecent(q) {
    const list = loadRecent().filter((r) => r !== q);
    saveRecent(list);
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
        minimumFractionDigits: 2,
      }).format((cents || 0) / 100);
    } catch (_) {
      return '€' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (m) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
  }

  /* Highlights matching tokens in a string, returning safe HTML with <mark>. */
  function highlightMatch(text, query) {
    const safe = escapeHtml(text || '').toLowerCase();
    const q = (query || '').trim().toLowerCase();
    if (!q || q.length < 2) return safe;
    // Split query into tokens (min 2 chars each)
    const tokens = q.split(/\s+/).filter((t) => t.length >= 2);
    if (!tokens.length) return safe;
    const pattern = tokens
      .sort((a, b) => b.length - a.length)
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|');
    const re = new RegExp('(' + pattern + ')', 'gi');
    return safe.replace(re, '<mark>$1</mark>');
  }

  function pluralize(n, singular, plural) {
    return n === 1 ? singular : plural;
  }

  function init() {
    const modal = document.getElementById('tcn-search-modal');
    if (!modal) return;

    const sheet     = modal.querySelector('[data-tcn-search-sheet]');
    const form      = modal.querySelector('[data-tcn-search-form]');
    const input     = modal.querySelector('[data-tcn-search-input]');
    const clearBtn  = modal.querySelector('[data-tcn-search-clear]');
    const closeBtn  = modal.querySelector('[data-tcn-search-close]');

    const idleView   = modal.querySelector('[data-tcn-search-idle]');
    const resultsView= modal.querySelector('[data-tcn-search-results]');
    const loadingEl  = modal.querySelector('[data-tcn-search-loading]');

    const recentBlock= modal.querySelector('[data-tcn-search-recent-block]');
    const recentList = modal.querySelector('[data-tcn-search-recent]');

    const productsBlock    = modal.querySelector('[data-tcn-search-products-block]');
    const productsContainer= modal.querySelector('[data-tcn-search-products]');
    const collectionsBlock = modal.querySelector('[data-tcn-search-collections-block]');
    const collectionsList  = modal.querySelector('[data-tcn-search-collections]');
    const pagesBlock       = modal.querySelector('[data-tcn-search-pages-block]');
    const pagesList        = modal.querySelector('[data-tcn-search-pages]');
    const queriesBlock     = modal.querySelector('[data-tcn-search-queries-block]');
    const queriesList      = modal.querySelector('[data-tcn-search-queries]');
    const allWrap          = modal.querySelector('[data-tcn-search-all-wrap]');
    const allLink          = modal.querySelector('[data-tcn-search-all]');
    const emptyEl          = modal.querySelector('[data-tcn-search-empty]');
    const emptyQuery       = modal.querySelector('[data-tcn-search-empty-query]');

    /* Pro features refs */
    const trendingItems    = modal.querySelectorAll('[data-tcn-search-trending-term]');
    const counterEl        = modal.querySelector('[data-tcn-search-counter]');
    const counterText      = modal.querySelector('[data-tcn-search-counter-text]');
    const previewEl        = modal.querySelector('[data-tcn-search-preview]');
    const previewMedia     = modal.querySelector('[data-tcn-search-preview-media]');
    const previewType      = modal.querySelector('[data-tcn-search-preview-type]');
    const previewTitle     = modal.querySelector('[data-tcn-search-preview-title]');
    const previewPrice     = modal.querySelector('[data-tcn-search-preview-price]');

    let focusedIndex = -1; // for keyboard nav
    let currentProductCards = []; // cached references for nav
    let currentProductData = [];  // raw product data for preview

    // Locale-aware base (e.g. "/it-it/search"), rendered server-side via
    // {{ routes.search_url }}. Fetching the un-prefixed "/search/suggest.json"
    // on a multi-locale shop triggers a redirect that mangles the nested
    // resources[type]=... query params into a broken single param — always
    // hit the correct locale path directly instead.
    const searchBase = (modal.dataset.searchUrl || '/search').replace(/\/+$/, '');

    /* ---------- Open / Close ---------- */
    function open() {
      if (modal.open) return;
      try { modal.showModal(); } catch (_) {}
      document.body.classList.add('tcn-search-open');
      renderRecent();
      // Focus input after animation frame
      requestAnimationFrame(() => input.focus());
    }
    function close() {
      try { modal.close(); } catch (_) {}
      document.body.classList.remove('tcn-search-open');
      // Reset
      input.value = '';
      if (clearBtn) clearBtn.hidden = true;
      showIdle();
    }
    function showIdle() {
      idleView.hidden = false;
      resultsView.hidden = true;
      loadingEl.hidden = true;
    }
    function showResults() {
      idleView.hidden = true;
      resultsView.hidden = false;
      loadingEl.hidden = true;
    }
    function showLoading() {
      loadingEl.hidden = false;
    }

    /* Open triggers (header search button + any [data-tcn-search-trigger]) */
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-tcn-search-trigger]');
      if (trigger) {
        e.preventDefault();
        open();
      }
    });

    /* Close: button, ESC, backdrop click */
    closeBtn?.addEventListener('click', close);
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
    modal.addEventListener('click', (e) => {
      // Backdrop click detection by coords (dialog backdrop is the area outside the sheet)
      if (e.target === modal) {
        const r = sheet.getBoundingClientRect();
        const inSheet = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inSheet) close();
      }
    });

    /* Clear */
    clearBtn?.addEventListener('click', () => {
      input.value = '';
      if (clearBtn) clearBtn.hidden = true;
      input.focus();
      showIdle();
    });

    /* ---------- Recent searches list ---------- */
    function renderRecent() {
      const recent = loadRecent();
      if (!recent.length) {
        recentBlock.hidden = true;
        return;
      }
      recentBlock.hidden = false;
      recentList.innerHTML = recent.map((q) => `
        <li class="tcn-search__recent-item">
          <a href="${escapeHtml(searchBase + '?q=' + encodeURIComponent(q))}" class="tcn-search__recent-link" data-tcn-recent-link>${escapeHtml(q)}</a>
          <button type="button" class="tcn-search__recent-remove" data-tcn-recent-remove="${escapeHtml(q)}" aria-label="Rimuovi">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </li>
      `).join('');
    }
    recentList?.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-tcn-recent-remove]');
      if (rm) {
        e.preventDefault();
        removeRecent(rm.dataset.tcnRecentRemove);
        renderRecent();
      }
    });

    /* ---------- Predictive search ---------- */
    let debounceTimer = null;
    let abortCtrl = null;
    let latestQuery = '';

    function setQueryParam(q) {
      const enc = encodeURIComponent(q);
      if (allLink) allLink.href = searchBase + '?q=' + enc;
    }

    async function predict(q) {
      latestQuery = q;
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();
      showLoading();
      try {
        const url = searchBase + '/suggest.json?q=' + encodeURIComponent(q)
                  + '&resources[type]=product,collection,page,article,query'
                  + '&resources[limit]=6'
                  + '&resources[options][unavailable_products]=last';
        const res = await fetch(url, { signal: abortCtrl.signal, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Bad response: ' + res.status);
        const data = await res.json();
        // Discard stale responses: only render if this is still the latest
        // query and the input hasn't moved on to something else meanwhile.
        if (q !== latestQuery || input.value.trim() !== q) return;
        renderResults(q, data.resources?.results || {});
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('[Tucano search] predictive error', err);
          if (q === latestQuery && input.value.trim() === q) renderResults(q, {});
        }
      }
    }

    function renderResults(q, results) {
      showResults();
      setQueryParam(q);

      const products    = results.products    || [];
      const collections = results.collections || [];
      const pages       = results.pages       || [];
      const articles    = results.articles    || [];
      const queries     = results.queries     || [];

      const total = products.length + collections.length + pages.length + articles.length + queries.length;

      /* Products */
      currentProductData = products;
      if (products.length) {
        productsBlock.hidden = false;
        productsContainer.innerHTML = products.map((p, idx) => {
          const img = p.featured_image?.url || p.image || '';
          const price = typeof p.price === 'number' ? p.price : (parseFloat(p.price) || 0) * 100;
          const titleHtml = highlightMatch((p.title || '').toLowerCase(), q);
          const typeText = (p.product_type || p.vendor || '').toLowerCase().replace(/tugo['’]/g, 'tugò');
          const metaText = typeText ? `${escapeHtml(typeText)} · ${formatMoney(price)}` : formatMoney(price);
          return `
            <a href="${escapeHtml(p.url)}" class="tcn-search__product" data-tcn-result-index="${idx}" role="option">
              <div class="tcn-search__product-media">
                ${img ? `<img src="${escapeHtml(img)}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
              </div>
              <div class="tcn-search__product-info">
                <span class="tcn-search__product-title">${titleHtml}</span>
                <span class="tcn-search__product-meta">${metaText}</span>
              </div>
            </a>
          `;
        }).join('');
        currentProductCards = Array.from(productsContainer.querySelectorAll('.tcn-search__product'));
        // Auto-focus first item + show preview
        focusResult(0);
      } else {
        productsBlock.hidden = true;
        currentProductCards = [];
        hidePreview();
      }

      /* Collections */
      if (collections.length) {
        collectionsBlock.hidden = false;
        collectionsList.innerHTML = collections.map((c) =>
          `<li><a href="${escapeHtml(c.url)}" class="tcn-search__link">${highlightMatch((c.title || '').toLowerCase(), q)}</a></li>`
        ).join('');
      } else { collectionsBlock.hidden = true; }

      /* Pages + Articles (combined) */
      const combinedPages = [...pages, ...articles];
      if (combinedPages.length) {
        pagesBlock.hidden = false;
        pagesList.innerHTML = combinedPages.map((p) =>
          `<li><a href="${escapeHtml(p.url)}" class="tcn-search__link">${highlightMatch((p.title || '').toLowerCase(), q)}</a></li>`
        ).join('');
      } else { pagesBlock.hidden = true; }

      /* Query suggestions */
      if (queries.length) {
        queriesBlock.hidden = false;
        queriesList.innerHTML = queries.map((qs) => {
          const text = qs.text || qs.styled_text || qs.title || '';
          return `<li><a href="${searchBase}?q=${encodeURIComponent(text)}" class="tcn-search__link">${highlightMatch(text.toLowerCase(), q)}</a></li>`;
        }).join('');
      } else { queriesBlock.hidden = true; }

      /* Counter (top bar, compatto) */
      if (counterEl && counterText) {
        if (total > 0) {
          counterEl.hidden = false;
          counterText.innerHTML = `<strong>${total}</strong> ${pluralize(total, 'risultato', 'risultati')}`;
        } else {
          counterEl.hidden = true;
        }
      }

      /* Empty state */
      if (total === 0) {
        emptyEl.hidden = false;
        if (emptyQuery) emptyQuery.textContent = '"' + q + '"';
        allWrap.hidden = true;
        hidePreview();
      } else {
        emptyEl.hidden = true;
        allWrap.hidden = false;
      }
    }

    /* ---------- Keyboard navigation in results ---------- */
    function focusResult(idx) {
      if (!currentProductCards.length) return;
      focusedIndex = Math.max(0, Math.min(idx, currentProductCards.length - 1));
      currentProductCards.forEach((c, i) => c.classList.toggle('is-focused', i === focusedIndex));
      // Show preview for focused product
      showPreviewForIndex(focusedIndex);
      // Ensure visible
      currentProductCards[focusedIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    function clearFocus() {
      focusedIndex = -1;
      currentProductCards.forEach((c) => c.classList.remove('is-focused'));
    }
    function showPreviewForIndex(idx) {
      if (!previewEl || idx < 0 || !currentProductData[idx]) {
        hidePreview();
        return;
      }
      const p = currentProductData[idx];
      const img = p.featured_image?.url || p.image || '';
      const price = typeof p.price === 'number' ? p.price : (parseFloat(p.price) || 0) * 100;
      previewMedia.innerHTML = img ? `<img src="${escapeHtml(img.replace(/\.(jpg|png|webp).*$/, '_800x.$1'))}" alt="${escapeHtml(p.title)}" loading="lazy">` : '';
      previewType.textContent = (p.product_type || p.vendor || 'tucano').toLowerCase().replace(/tugo['’]/g, 'tugò');
      previewTitle.textContent = (p.title || '').toLowerCase();
      previewPrice.textContent = formatMoney(price);
      previewEl.hidden = false;
      previewEl.setAttribute('aria-hidden', 'false');
    }
    function hidePreview() {
      if (!previewEl) return;
      previewEl.hidden = true;
      previewEl.setAttribute('aria-hidden', 'true');
    }

    /* Mouse hover on a product card → update preview */
    productsContainer.addEventListener('mouseover', (e) => {
      const card = e.target.closest('.tcn-search__product');
      if (!card) return;
      const idx = parseInt(card.dataset.tcnResultIndex, 10);
      if (!Number.isNaN(idx)) focusResult(idx);
    });

    /* Input keyboard nav */
    input.addEventListener('keydown', (e) => {
      if (!currentProductCards.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusResult(focusedIndex < 0 ? 0 : focusedIndex + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusResult(focusedIndex <= 0 ? 0 : focusedIndex - 1);
      } else if (e.key === 'Enter') {
        if (focusedIndex >= 0 && currentProductCards[focusedIndex]) {
          e.preventDefault();
          addRecent(input.value);
          window.location.href = currentProductCards[focusedIndex].href;
        }
        // else: let the form submit naturally to /search?q=...
      }
    });

    /* Input handler with debounce */
    input.addEventListener('input', () => {
      const q = input.value.trim();
      if (clearBtn) clearBtn.hidden = q.length === 0;
      clearTimeout(debounceTimer);

      if (!q) {
        showIdle();
        return;
      }
      if (q.length < 2) {
        // Wait for at least 2 chars to predict
        showIdle();
        return;
      }
      debounceTimer = setTimeout(() => predict(q), DEBOUNCE_MS);
    });

    /* Form submit → save recent + go to /search?q= */
    form.addEventListener('submit', () => {
      addRecent(input.value);
    });

    /* Save recent also on product/link click within results */
    resultsView.addEventListener('click', (e) => {
      if (e.target.closest('a')) addRecent(input.value);
    });

    /* ---------- Trending buttons → fill input + trigger search ---------- */
    trendingItems.forEach((btn) => {
      btn.addEventListener('click', () => {
        const term = btn.dataset.tcnSearchTrendingTerm || btn.textContent.trim();
        input.value = term;
        if (clearBtn) clearBtn.hidden = false;
        predict(term);
        input.focus();
      });
    });

    /* ---------- Recent searches: click fills input + searches ---------- */
    recentList?.addEventListener('click', (e) => {
      const link = e.target.closest('[data-tcn-recent-link]');
      if (link) {
        // Default <a> navigation will take user to /search — but if we want
        // to keep the modal open and predict, intercept:
        e.preventDefault();
        const q = link.textContent.trim();
        input.value = q;
        if (clearBtn) clearBtn.hidden = false;
        predict(q);
        input.focus();
      }
    });

    /* ---------- ⌘K / Ctrl+K global shortcut ---------- */
    document.addEventListener('keydown', (e) => {
      const isCmd = e.metaKey || e.ctrlKey;
      if (isCmd && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (modal.open) close();
        else open();
      }
      // "/" key opens search (common shortcut on news/ecom sites)
      if (e.key === '/' && !modal.open) {
        const tag = (e.target.tagName || '').toLowerCase();
        const isEditable = ['input', 'textarea', 'select'].includes(tag) || e.target.isContentEditable;
        if (!isEditable) {
          e.preventDefault();
          open();
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
